import { background, baseStyle, extendTheme, textDecoration } from "@chakra-ui/react"
import { mode, transparentize } from "@chakra-ui/theme-tools"

 const theme = extendTheme({
    breakpoints: {
      xs: "1px",
      sm: "320px",
      md: "1025px",
      lg: "1180px",
      xl: "1200px",
      "2xl": "1536px",
    },
    fonts: {
      body: "Poppins, sans-serif",
      heading: "Poppins, sans-serif",
      bodySecondary: "Inter, sans-serif",
    },
    colors: {
      imagekitBlue: {
        50: "#f3f8ff",
        100: "#f3f8ff",
        200: "#cddcf7",
        300: "#9bb9ee",
        400: "#6896e6",
        500: "#3673dd",
        600: "#0450d5",
        700: "#0340aa",
        800: "#002d7d",
        900: "#002d7d",
      },
      imagekitGray: {
        50: "#f7fafc",
        100: "#f7fafc",
        200: "#edf2f7",
        300: "#e1e4eb",
        400: "#cdd5df",
        500: "#a0aec0",
        600: "#718096",
        700: "#4a5568",
        800: "#2d3748",
        900: "#1a202c",
      },
      surfaceSecondary: "#F3F8FF"
    },
    components: {
      Accordion: {
        baseStyle: {
          button: {
            fontFamily: "heading",
            _focus: {
              boxShadow: "none",
            },
          },
          panel: {
            fontFamily: "bodySecondary",
          }
        },
        variants: {
          navbar: (props) => {
            return {
              container: {
                bg: "transparent",
                border: "none",
                boxShadow: "none",
                position: "relative",
                _after: {
                  content: "''",
                  position: "absolute",
                  bottom: 0,
                  left: "16px",
                  right: "16px",
                  height: "1px",
                  bg: "blackAlpha.300",
                },
                _last: {
                  _after: {
                    display: "none"
                  }
                }
              },
              button: {
                fontWeight: "bold",
                px: 4,
                py: 6,
                _hover: {
                  bg: "transparent"
                },
                color: "imagekitGray.900",
                _expanded: {
                  bg: "#EBEFF2",
                  boxShadow: "0px 2px 8px 0px #0000001A",
                },
              },
              panel: {
                bg: "surfaceSecondary",
                px: 4,
                py: 4,
              }
            }
          },
          featureShowcase: () => {
            return {
              container: {
                _first: {
                  borderTopWidth: "0px"
                },
                _last: {
                  borderBottomWidth: "0px"
                },
                py: 4,
              },
              button: {
                fontWeight: "bold",
                px: 0,
                py: 2.5,
                _hover: {
                  bg: "transparent"
                }
              },
              panel: {
                px: 0
              }
            }
          }
        }
      },
      Button: {
        baseStyle: (props) => { 
          const { colorScheme: c } = props
          return {
            textTransform: "capitalize",
            borderRadius: "16px",
            fontWeight: "500",
            _focus: {
              boxShadow: "none",
              outlineWidth: "2px",
              outlineOffset: "2px",
              outlineStyle: "solid",
              outlineColor: mode(`${c}.600`, `${c}.300`)(props),
            },
            _active: {
              boxShadow: "inset 4px 4px 16px 0 var(--chakra-colors-blackAlpha-300)",
            }
          }
        },
        sizes: {
          lg: {
            h: 12,
            minW: 12,
            fontSize: "16px",
            px: 7,
            py: 7,
          },
        },
        variants: {
          ghost: (props) => {
            const { colorScheme: c, theme } = props

            const darkHoverBg = transparentize(`${c}.200`, 0.12)(theme)
            const darkActiveBg = transparentize(`${c}.200`, 0.24)(theme)

            return {
              color: mode(`${c}.600`, `${c}.200`)(props),
              bg: "transparent",
              _hover: {
                bg: mode(`${c}.50`, darkHoverBg)(props),
              },
              _active: {
                bg: mode(`${c}.100`, darkActiveBg)(props),
              },
            }
          },
          solid: (props) => {
            const { colorScheme: c } = props

            const accessibleColorMap = {
              yellow: {
                bg: "yellow.400",
                color: "black",
                hoverBg: "yellow.500",
                activeBg: "yellow.600",
              },
              cyan: {
                bg: "cyan.400",
                color: "black",
                hoverBg: "cyan.500",
                activeBg: "cyan.600",
              },
              imagekitBlue: {
                bg: "imagekitBlue.600",
                color: "white",
                hoverBg: "imagekitBlue.700",
                activeBg: "imagekitBlue.600",
              },
              imagekitWhite: {
                bg: "white",
                color: "imagekitGray.900",
                hoverBg: "imagekitBlue.100",
                hoverColor: "imagekitBlue.600",
                activeBg: "imagekitBlue.100",
                activeColor: "imagekitBlue.700",
              }
            }

            const {
              bg = `${c}.500`,
              color = "white",
              hoverBg = `${c}.600`,
              hoverColor = color,
              activeBg = `${c}.700`,
              activeColor = color,
            } = accessibleColorMap[c] ?? {}

            const background = mode(bg, `${c}.200`)(props)

            return {
              bg: background,
              color: mode(color, `gray.800`)(props),
              _hover: {
                bg: mode(hoverBg, `${c}.300`)(props),
                color: mode(hoverColor, `${c}.600`)(props),
                _disabled: {
                  bg: background,
                },
              },
              _active: {
                bg: mode(activeBg, `${c}.400`)(props),
                color: mode(activeColor, `${c}.600`)(props),
              },
              _focus: {
                outlineColor: mode(hoverBg, `${c}.300`)(props),
              },
            }
          },
          outline: (props) => {
            const { colorScheme: c } = props

            const accessibleColorMap = {
              imagekitWhite: {
                color: "white",
                hoverBg: "whiteAlpha.300",
                activeBg: "whiteAlpha.300",
                focusOutline: "blackAlpha.300"
              }
            }
            const {
              bg = "transparent",
              color = `${c}.600`,
              hoverBg = `${c}.100`,
              hoverColor = color,
              activeBg = `${c}.200`,
              activeColor = color,
              focusOutline = activeBg,
            } = accessibleColorMap[c] ?? {}

            return {
              border: "1px solid",
              borderColor: "currentColor",
              color,
              bg,
              _hover: {
                bg: hoverBg,
                color: hoverColor,
              },
              _active: {
                bg: activeBg,
                color: activeColor,
              },
              _focus: {
                outlineColor: focusOutline,
              },
            }
          },
          navBarItem: (props) => {
            const { colorScheme: c, theme } = props
            return {
              _focus: {
                boxShadow: "none",
                color: mode(`${c}.600`, `${c}.200`)(props),
                outlineColor: "transparent",
              },
              fontWeight: "normal",
              px: 2,
              color: mode("imagekitGray.900", "white")(props),
              _hover: {
                color: mode(`${c}.600`, `${c}.200`)(props),
                textDecoration: "none",
              },
              _active: {
                boxShadow: "none",
              }
            }
          },
          sdk: (props) => {
            const { colorScheme: c, theme } = props
            return {
              _focus: {
                boxShadow: "none",
              },
              fontWeight: "bold",
              px: 4,
              py: 2,
              color: "imagekitGray.900",
              fontSize: "sm",
              bg: "imagekitBlue.200",
              borderRadius: "full",
              _hover: {
                background: transparentize("imagekitBlue.200", 0.5)(theme),
                color: mode(`${c}.600`, `${c}.200`)(props),
                textDecoration: "none",
              },
              _active: {
                boxShadow: "none",
              }
            }
          },
          navbarIcon: {
            _focus: {
              outlineColor: "transparent",
            },
            _active: {
              boxShadow: "none",
            }
          }
        }
      },
      Badge: {
        variants: {
          navbar: (props) => {
            const accessibleColorMap = {
              "imagekitBlue": {
                bg: "#0450d5",
                color: "white"
              }
            }

            const { colorScheme: c, theme } = props

            let dark = transparentize(`${c}.500`, 0.6)(theme)
            let bg = `${c}.500`
            let color = "white"

            if (accessibleColorMap[c]) {
              dark = transparentize(`${accessibleColorMap[c].bg}`, 0.6)(theme)
              bg = accessibleColorMap[c].bg
              color = accessibleColorMap[c].color
            }

            return {
              borderRadius: 2,
              px: 2,
              py: 0,
              bg: mode(bg, dark)(props),
              color: mode(color, `whiteAlpha.800`)(props),
              fontWeight: "normal",
              fontSize: "12px",
              lineHeight: "18px",
              textTransform: "initial",
            }
          },
          featureShowcase: (props) => {
            const accessibleColorMap = {
              "imagekitBlue": {
                bg: "#0450d5",
                color: "white"
              },
              imagekitYellow: {
                bg: "#ffefad",
                color: "#1a202c"
              },
              imagekitGreen: {
                bg: "#baffde",
                color: "#1a202c"
              },
              imagekitPurple: {
                bg: "#d6e0ff",
                color: "#1a202c"
              }
            }
            const { colorScheme: c, theme } = props

            let dark = transparentize(`${c}.500`, 0.6)(theme)
            let bg = `${c}.500`
            let color = "white"

            if (accessibleColorMap[c]) {
              dark = transparentize(`${accessibleColorMap[c].bg}`, 0.6)(theme)
              bg = accessibleColorMap[c].bg
              color = accessibleColorMap[c].color
            }

            return {
              borderRadius: "full",
              px: 5,
              py: 1,
              fontSize: "1rem",
              bg: mode(bg, dark)(props),
              color: mode(color, `whiteAlpha.800`)(props),
              textTransform: "initial",
            }
          },
          toolState: (props) => { 
            const { state } = props

            const stateColorMap = {
              "pending": "imagekitBlue.600",
              "processing": "yellow.600",
              "completed": "green.500",
              "error": "red.500",
            }

            return {
              borderRadius: "3px",
              px: 3,
              py: 0.5,
              fontSize: "0.8rem",
              fontWeight: "medium",
              bg: "transparent",
              border: "1px solid",
              borderColor: stateColorMap[state],
              color: stateColorMap[state],
              textTransform: "initial",
            }
          }
        },
      },
      Popover: {
        variants: {
          navBarItem: (props) => {
            return {
              content: {
                background: "surfaceSecondary",
                p: 0,
                borderRadius: "1rem",
                _focus: {
                  boxShadow: "none"
                }
              },
              body: {
                p: 6,
                borderColor: "imagekitGray.400"
              },
              arrow: {
                background: "surfaceSecondary"
              }
            }
          }
        }
      },
      Table: {
        variants: {
          toolResult: (props) => { 
            const { colorScheme: c } = props
  
            return {
              th: {
                color: mode("gray.600", "gray.400")(props),
                borderBottom: "1px",
                borderLeft: "1px",
                borderColor: mode(`${c}.100`, `${c}.700`)(props),
              },
              tr: {
                borderBottom: "1px",
                borderLeft: "1px",
                borderRight: "1px",
                borderColor: mode(`${c}.100`, `${c}.700`)(props),
                "&:first-of-type": {
                  borderTop: "1px",
                  borderColor: mode(`${c}.100`, `${c}.700`)(props),
                }
              },
              caption: {
                color: mode("gray.600", "gray.100")(props),
              },
              tfoot: {
                tr: {
                  "&:last-of-type": {
                    th: {
                      borderLeft: "0px"
                    },
                  },
                },
              },
            }
          }
        }
      }
    },
    styles: {
      global: {
        body: {
          lineHeight: "initial",
        },
        h1: {
          fontWeight: "bold",
          fontSize: "4xl",
        },
        h2: {
          fontWeight: "bold",
          fontSize: "2xl",
        },
        h3: {
          fontWeight: "bold",
          fontSize: "lg",
        },
        h4: {
          fontWeight: "bold",
          fontSize: "md",
        },
        h5: {
          fontWeight: "bold",
          fontSize: "sm",
        },
        h6: {
          fontWeight: "bold",
          fontSize: "xs",
        },
        input: {
          lineHeight: 1.1,
          height: "41px",
          fontSize: "13.33px",
        },
        hr: {
          all: "revert",
        },
        img: {
          display: "inline-block",
        },
        video:
        {
          display: "inline-block",
        }
      },
    },
  })

  export default theme